import type { Cluster } from '@googlemaps/markerclusterer';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import {
  clusterMarkerFormattersHTML,
  MarkerType,
} from '@apps/main/modules/maps/markerFormatters';

import type {
  DestinationStaticInfo,
  ContentApiImage,
  IngredientId,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';
import type { ClusterOptions, Marker } from '@libs/maps';
import { MapTypeId } from '@libs/maps';

import type {
  ActivityMapCardData,
  ActivityType,
  HighlightedActivity,
} from '../../models';

const { t } = i18n.global;

const ACTIVITY_SELECTION_MARKERS = [
  MarkerType.Activity,
  MarkerType.Golf,
  MarkerType.Destination,
] as string[];

export function getActivitySelectionMarkers(
  markers: Marker[],
  highlightedActivity: HighlightedActivity | undefined,
  topProducts?: string[]
): Marker[] {
  return markers
    .filter(
      (marker) =>
        marker.useContentFormatter &&
        ACTIVITY_SELECTION_MARKERS.includes(marker.useContentFormatter)
    )
    .map((marker) => {
      const isTopMarker = topProducts?.includes(marker.data.hotelCode);

      const clusteredMarker = {
        ...marker,
        cluster: !isTopMarker,
      };
      if (marker.useClusterContentFormatter === undefined) {
        return {
          ...clusteredMarker,
          cluster: false,
        };
      }
      if (
        highlightedActivity &&
        marker.key.includes(highlightedActivity?.activityCode)
      ) {
        const baseCss = marker.cssClass ?? '';
        const cssClass = `${baseCss} highlighted`;
        // NOTE: Change of key is required to force redraw in frontend libs
        return {
          ...clusteredMarker,
          cssClass,
          key: `${marker.key} highlighted`,
          cluster: false,
          displayPriority: 4,
        };
      }
      return clusteredMarker;
    });
}

export function getMarkerFormatters(
  mapTypeId: MapTypeId
): Record<
  MarkerType.Activity | MarkerType.Golf | MarkerType.Destination,
  (marker: Marker) => HTMLDivElement
> {
  const factoryFn = (marker: Marker): HTMLDivElement => {
    const div = document.createElement('div');

    div.className = `activity-selection-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
      <div class="activity-selection-map-marker-label">
        <span>${marker.data?.formattedPrice}</span>
      </div>
    `;
    return div;
  };
  const accommodationFactoryFn = (marker: Marker): HTMLDivElement => {
    const url =
      mapTypeId === MapTypeId.HYBRID
        ? 'https://cdn.sanity.io/images/inntox6v/production/542dfdb14801c9389bea94a1abbf7cd836404706-44x44.svg'
        : 'https://cdn.sanity.io/images/inntox6v/production/2a4aafbdfb33ae617ca6f7d965283fc313b66b53-44x44.svg';
    const alt = `${t('a11y.destination_location_marker')} ${marker.subtitle}`;
    const div = document.createElement('div');

    div.className = `trip-summary-accommodation-marker ${
      marker.cssClass || ''
    }`;
    div.innerHTML = `<img src="${url}" alt="${alt}" width="35" height="35">`;
    return div;
  };
  return {
    [MarkerType.Activity]: factoryFn,
    [MarkerType.Golf]: factoryFn,
    [MarkerType.Destination]: accommodationFactoryFn,
  };
}

export function getActivityMapCardData(
  activityMapCard: HighlightedActivity | undefined,
  getStaticInfo: (
    activityCode: string,
    ingredientId: IngredientId
  ) => DestinationStaticInfo | OverviewDestinationStaticInfo | undefined,
  getImage: (
    image: ContentApiImage,
    activityCode?: string,
    width?: number,
    height?: number
  ) => string
): ActivityMapCardData | undefined {
  if (!activityMapCard) {
    return;
  }

  const { ingredientId, activityCode } = activityMapCard;
  const staticInfo = getStaticInfo(activityCode, ingredientId);

  if (!staticInfo || !staticInfo.images || !staticInfo.images[0]) {
    return;
  }

  return {
    ingredientId: ingredientId,
    activityCode: activityCode,
    activityName: staticInfo.name,
    starsRating: staticInfo.semboSunRating,
    imageUrl: getImage(staticInfo.images[0]),
    activityType: staticInfo.typeOfHotel as ActivityType,
  };
}

export function getHighlightedActivity(
  activityCode: string | undefined,
  ingredientId: IngredientId | undefined
): HighlightedActivity | undefined {
  if (activityCode && ingredientId) {
    return {
      activityCode,
      ingredientId,
      showActivityMapCard: false,
    };
  } else {
    return undefined;
  }
}

export function getClusterOptions(): ClusterOptions {
  return {
    minPoints: 10,
    radius: 120,
  };
}

export function getClusterActivityMarkerFormatters(): Record<
  MarkerType.Activity | MarkerType.Golf,
  (marker: Marker, cluster: Cluster) => HTMLDivElement
> {
  return {
    [MarkerType.Activity]: clusterMarkerFormattersHTML[MarkerType.Activity],
    [MarkerType.Golf]: clusterMarkerFormattersHTML[MarkerType.Golf],
  };
}
